import { PartImageItem } from '@/api/generatedApi';
import { Thumbnail } from '@/components/Thumbnail';
import StraightenIcon from '@mui/icons-material/Straighten';
import { Box, Stack, Typography } from '@mui/material';
import {
  canMeasure,
  IncludeCheckbox,
  ItemCard,
  ItemHeader,
  ItemIconAction,
  NO_PICK,
  PartPicker,
  photoList,
} from './PartImageItemControls';

const WHOLE_PHOTO_THUMB_SIZE = 88;
const CHECKBOX_WIDTH = 32;

export interface WholePhotoRowProperties {
  item: PartImageItem;
  photo?: File;
  pick: string;
  included: boolean;
  busy: boolean;
  onPick: (niin: string) => void;
  onToggle: () => void;
  onMeasure: () => void;
}

export const WholePhotoRow = ({
  item,
  photo,
  pick,
  included,
  busy,
  onPick,
  onToggle,
  onMeasure,
}: WholePhotoRowProperties) => {
  const matched = item.candidates.length > 0;
  return (
    <ItemCard data-testid='part-image-whole-photo'>
      {matched && (
        <IncludeCheckbox
          label='Include the whole photo in batch'
          included={included}
          picked={pick !== NO_PICK}
          busy={busy}
          onToggle={onToggle}
        />
      )}
      {!matched && <Box sx={{ width: CHECKBOX_WIDTH, flexShrink: 0 }} />}
      {photo && <Thumbnail file={photo} size={WHOLE_PHOTO_THUMB_SIZE} noLink />}
      <Stack gap='8px' sx={{ flex: 1, minWidth: 0 }}>
        <ItemHeader
          title='Whole photo'
          subtitle={`everything in ${photoList(item.photoNumbers)} as one part`}
          description={item.description}
          actions={
            canMeasure(item) && (
              <ItemIconAction
                label='Measure'
                icon={<StraightenIcon fontSize='small' />}
                testId='part-image-item-measure'
                disabled={busy}
                onClick={onMeasure}
              />
            )
          }
        />
        {matched && <PartPicker candidates={item.candidates} pick={pick} disabled={busy} onPick={onPick} />}
        {!matched && (
          <Typography variant='body2' color='text.secondary' data-testid='part-image-whole-photo-no-match'>
            No FLIS match for the photo as a whole. Measure it, or pick parts from the items below.
          </Typography>
        )}
      </Stack>
    </ItemCard>
  );
};
